/**
 * BoneTwistDisperser3D's two rules that need a sibling property: an index past the count that sizes
 * its container, which Godot refuses, at two depths. Godot's saver cannot trip either: `setting_count`
 * is ClassDB-bound (`ADD_ARRAY_COUNT`, :561), so `Object::get_property_list` puts it ahead of the
 * leaves, and both counts are read off the live vectors at save time.
 */
import { ruleRegistry } from '../../../../linter/RuleRegistry.js';
import { listWrittenIndices } from '../../../../linter/reportedIndices.js';
import { descendsFromClass } from '../../../../godot/classdb/classBaseTypes.js';
import { ruleCount } from '../../../../godot/variant/intSlots.js';
import { indexedKeyRegex } from '../../../../godot/classdb/indexedKey.js';
import { stringToInt } from '../../../../godot/string/string.js';
import { armEmits, reportArm } from '../../../../linter/ruleArms.js';
const arms = {
    settingIndexOutOfRange: {
        severity: 'error',
        ruleName: 'bonetwistdisperser3d-setting-index-out-of-range',
        grounding: { kind: 'engine', at: 'bone_twist_disperser_3d.cpp:39' },
    },
    jointIndexOutOfRange: {
        severity: 'error',
        ruleName: 'bonetwistdisperser3d-joint-index-out-of-range',
        grounding: { kind: 'engine', at: 'bone_twist_disperser_3d.cpp:502' },
    },
};
/**
 * Any `settings/<i>/…` leaf, with the index text captured. `_set` reads both index positions with a
 * bare `path.get_slicec('/', n).to_int()` into an `int` and no validity gate
 * (bone_twist_disperser_3d.cpp:37, :66), so the grammar is the whole segment, and
 * {@link stringToInt} reads the number Godot stores.
 */
const SETTING_KEY_RE = indexedKeyRegex('^settings/(#)/', 'to_int');
/**
 * The one nested leaf a scene can write, with both index texts captured. `prop =
 * path.get_slicec('/', 4)` (:67) reads that segment alone, so a tail below it reaches the setter.
 */
const JOINT_AMOUNT_KEY_RE = indexedKeyRegex('^settings/(#)/joints/(#)/twist_amount(?:/|$)', 'to_int');
/**
 * The per-setting joint array size, which every joint index is measured against. `what` is one
 * segment (:38, :63), so `joint_count/extra` sizes the array too.
 */
const JOINT_COUNT_KEY_RE = indexedKeyRegex('^settings/(#)/joint_count(?:/|$)', 'to_int');
/**
 * Which setting each `joint_count` key sizes, keyed by the resolved index, not the text: `_set` uses
 * `to_int` (bone_twist_disperser_3d.cpp:37), so `settings/00/joint_count` sizes setting 0. A later
 * key wins, as `Object.keys` keeps insertion order and Godot applies properties in file order.
 */
function resolveJointCounts(properties) {
    const counts = new Map();
    for (const key of Object.keys(properties)) {
        const match = JOINT_COUNT_KEY_RE.exec(key);
        if (!match)
            continue;
        const settingIndex = stringToInt(match[1]);
        // A negative index is refused before the count is read at all.
        if (settingIndex < 0)
            continue;
        // A malformed count is its own validator's error. Ignoring it here leaves
        // the setting at its zero default rather than inventing a ceiling. A
        // negative one is refused outright (`ERR_FAIL_COND(p_count < 0)`, :487), so
        // `ruleCount` reads the size the vector keeps rather than the authored text.
        const count = ruleCount(properties[key]);
        if (count === null)
            continue;
        counts.set(settingIndex, count);
    }
    return counts;
}
function checkBoneTwistDisperser3D(context) {
    const diagnostics = [];
    const { node } = context;
    const rawProps = node.rawProperties;
    // Absent means zero: `LocalVector<BoneTwistDisperser3DSetting *> settings`
    // (bone_twist_disperser_3d.h:86) starts empty, which is the XML's default="0".
    const settingCountRaw = rawProps.setting_count;
    // `ruleCount`, not `ruleInt`: `set_setting_count` refuses a negative (`ERR_FAIL_COND(p_count < 0)`,
    // :650), so `settings` keeps its loaded length. Counting against -1 names a size Godot never held,
    // beside the `enforced:` validator that already errored.
    const settingCount = ruleCount(settingCountRaw);
    // Neither an unreadable count nor a non-finite one is a ceiling to count
    // against. Each is already its own validator's diagnostic.
    if (settingCount === null)
        return diagnostics;
    const jointCounts = resolveJointCounts(rawProps);
    /** Each out-of-range setting index as the file writes it, to the setting it resolves to. */
    const outOfRangeSettings = new Map();
    /** `<setting>/<joint>` as the file writes both, to the pair they resolve to. */
    const outOfRangeJoints = new Map();
    for (const key of Object.keys(rawProps)) {
        const indexed = SETTING_KEY_RE.exec(key);
        if (!indexed)
            continue;
        const settingText = indexed[1];
        const settingIndex = stringToInt(settingText);
        // A negative index is the validator's error, against the same ERR_FAIL_INDEX_V, and reporting
        // it again here would double up on one defect.
        if (settingIndex < 0)
            continue;
        // `_set` opens with `ERR_FAIL_INDEX_V(which, (int)settings.size(), false)`
        // (bone_twist_disperser_3d.cpp:39), and only `set_setting_count` (:649-666) resizes `settings`,
        // so every leaf of an index at or past the count is dropped on load.
        if (settingIndex >= settingCount) {
            outOfRangeSettings.set(settingText, settingIndex);
            // The whole setting is refused, so its joints never get their own turn.
            continue;
        }
        const joint = JOINT_AMOUNT_KEY_RE.exec(key);
        if (!joint)
            continue;
        const jointText = joint[2];
        const jointIndex = stringToInt(jointText);
        if (jointIndex < 0)
            continue;
        // `set_joint_twist_amount` guards with `ERR_FAIL_INDEX(p_joint, (int)joints.size())` (:502), and
        // only `set_joint_count` (:485-491) resizes it. The file's count governs: it is pushed (:158) before
        // its joints (:159-164), and `_update_joints` waits for ENTER_TREE (:220-222, :597). Absent means
        // zero: `LocalVector<DisperseJointSetting> joints` (bone_twist_disperser_3d.h:69) starts empty.
        const jointCount = jointCounts.get(settingIndex) ?? 0;
        if (jointIndex >= jointCount) {
            outOfRangeJoints.set(`${settingText}/${jointText}`, [settingIndex, jointIndex]);
        }
    }
    if (outOfRangeSettings.size > 0) {
        const indices = listWrittenIndices(outOfRangeSettings);
        reportArm(diagnostics, arms.settingIndexOutOfRange, node, `BoneTwistDisperser3D setting index(es) ${indices} fall outside setting_count ` +
            `(${settingCount}). BoneTwistDisperser3D::_set opens with ERR_FAIL_INDEX_V(which, ` +
            'settings.size(), false) (bone_twist_disperser_3d.cpp:39), so no setter runs and ' +
            'these settings/<i>/… values are silently dropped on load.');
    }
    if (outOfRangeJoints.size > 0) {
        // Ordered by both resolved indices: a lexicographic sort puts `0/10` before `0/2`.
        const pairs = listWrittenIndices(outOfRangeJoints);
        reportArm(diagnostics, arms.jointIndexOutOfRange, node, `BoneTwistDisperser3D joint(s) ${pairs} (setting/joint) ` +
            'set twist_amount past their own joint_count. set_joint_twist_amount guards with ' +
            'ERR_FAIL_INDEX(p_joint, joints.size()) (bone_twist_disperser_3d.cpp:502) and the ' +
            'vector is sized only by joint_count (:485-491), so the amount is dropped on load.');
    }
    return diagnostics;
}
// No rule for `damping_curve` beside `joints/<j>/twist_amount`: `_update_curve` (:389-399) overwrites
// each amount from the curve, but `_validate_dynamic_prop` only adds `PROPERTY_USAGE_READ_ONLY`
// (:208-210) and keeps STORAGE, so Godot writes the pair on every save that uses a curve.
const boneTwistDisperser3DValidationRule = {
    meta: {
        name: 'valid-bonetwistdisperser3d-settings',
        description: "Validates BoneTwistDisperser3D's settings/<i>/… indices against setting_count and its joint twist amounts against each setting's joint_count",
        category: 'validation',
        applicableNodeTypeMatcher: (nodeType) => descendsFromClass(nodeType, 'BoneTwistDisperser3D'),
        emits: armEmits(arms),
    },
    check: checkBoneTwistDisperser3D,
};
ruleRegistry.register(boneTwistDisperser3DValidationRule);
//# sourceMappingURL=linter.js.map